import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/en/poa/jurisdiction/$country")({
  loader: async ({ params }) => {
    const { POA_JURISDICTIONS, getPoaJurisdiction } = await import("@/content/poa-corpus/jurisdictions");
    const j = getPoaJurisdiction(params.country);
    if (!j) throw notFound();
    return { j, siblings: POA_JURISDICTIONS.filter((x) => x.slug !== j.slug).map((x) => ({ slug: x.slug, label: x.nameEn })) };
  },
  component: PoaJurisdictionPageEn,
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Destination not found" }, { name: "robots", content: "noindex" }] };
    const site = getActiveSiteUrl();
    const { j } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `Power of Attorney from Thailand for ${j.nameEn} — Certification Chain`,
        description: j.summaryEn,
        path: `/en/poa/jurisdiction/${params.country}`,
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: `/poa/jurisdiction/${params.country}` }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildSpeakableSchema(`/en/poa/jurisdiction/${params.country}`)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Power of Attorney", url: `${site}/en/poa` },
              { name: j.nameEn, url: `${site}/en/poa/jurisdiction/${params.country}` },
            ]),
          ),
        },
      ],
    };
  },
});

function PoaJurisdictionPageEn() {
  const { j, siblings } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/poa/faq" className="hover:underline">Power of Attorney</a> <span>/</span> <span>{j.nameEn}</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Power of attorney signed in Thailand for use in {j.nameEn}
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">{j.summaryEn}</p>

        <section aria-label="Key facts" className="mb-8 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <tbody>
              <tr className="border-b">
                <th scope="row" className="w-1/3 py-2 pr-3 text-left align-top font-semibold">Destination is an Apostille party</th>
                <td className="py-2 align-top text-muted-foreground">{j.hagueParty ? "Yes" : "No"}</td>
              </tr>
              <tr className="border-b">
                <th scope="row" className="py-2 pr-3 text-left align-top font-semibold">Apostille and Thailand</th>
                <td className="py-2 align-top text-muted-foreground">Enters into force on 28 February 2027</td>
              </tr>
              <tr className="border-b">
                <th scope="row" className="py-2 pr-3 text-left align-top font-semibold">Steps in the chain</th>
                <td className="py-2 align-top text-muted-foreground">{j.chainEn.length} steps, excluding the queue at the receiving body</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section aria-label="Certification chain" className="mb-8">
          <h2 className="mb-3 text-xl font-bold">The certification chain step by step</h2>
          <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
            {j.chainEn.map((s: string, i: number) => <li key={i}>{s}</li>)}
          </ol>
        </section>

        <section aria-label="Common use cases" className="mb-8">
          <h2 className="mb-3 text-xl font-bold">Common use cases</h2>
          <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
            {j.useCasesEn.map((s: string, i: number) => <li key={i}>{s}</li>)}
          </ul>
        </section>

        <ServiceAdviceBlock items={j.adviceEn} locale="en" />
        <ServiceCautionBlock items={j.cautionsEn} locale="en" />

        <section aria-label="Sources for this page" className="mt-8">
          <h2 className="mb-3 text-xl font-bold">Country-specific sources</h2>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {j.sources.map((s: { label: string; url: string }) => (
              <li key={s.url}>
                <a href={s.url} rel="noopener nofollow" target="_blank" className="underline hover:text-foreground">{s.label}</a>
              </li>
            ))}
          </ul>
        </section>

        <section aria-label="Other destinations" className="mt-10">
          <h2 className="mb-3 text-xl font-bold">Other destination countries</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {siblings.map((x: { slug: string; label: string }) => (
              <a key={x.slug} href={`/en/poa/jurisdiction/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.label}
              </a>
            ))}
          </div>
        </section>

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Let us sequence the chain for you</h2>
          <p className="text-sm text-muted-foreground">
            Tell us the purpose and the receiving body in {j.nameEn} by phone, LINE or email, Mon-Sat 9:00-18:00.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact us</a>
        </section>
        <div className="mt-8 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href={`/poa/jurisdiction/${j.slug}`} className="text-primary underline">อ่านหน้านี้เป็นภาษาไทย →</a>
        </div>
        <FaqSourceNote topic="poa" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}